import { useCallback, useMemo } from 'react';

import { Slider } from 'antd';

import {
  isoDateFromMs,
  lerpWindowDateMs,
  parseRemoteWindow,
  REMOTE_SCRUB_SLIDER_STEPS,
  sliderValueToT01,
  t01ToSliderValue,
} from '@/features/map/utils/sarRemoteScrubUtils';
import type { AoiRemoteTimeWindow } from '@/types/flood.types';

export interface RemoteSceneColumnTimeScrubberProps {
  timeWindow: AoiRemoteTimeWindow;
  /** 0..1 theo dòng thời gian — dùng mô phỏng vùng ngập (sub-day, không cô vào 1 ngày). */
  t01: number;
  onT01Change: (t01: number) => void;
  /** Ngày theo mốc (cho label / API ảnh) — từ lerp, có thể trùng khi cùng lịch. */
  onIsoChange: (iso: string) => void;
  /** DD/MM/YYYY từ YYYY-MM-DD (local). */
  formatDay: (ymd: string) => string;
  /** Thanh kéo (a11y). */
  ariaLabel: string;
}

const TRACK_CLASS =
  'mt-2 pt-2 border-t border-[var(--glass-border)] [&_.ant-slider-rail]:!bg-slate-700/60 [&_.ant-slider-track]:!bg-sky-500/90 [&_.ant-slider-handle::after]:!shadow-sky-400/30 [&_.ant-slider-handle]:!border-sky-400/80';

/**
 * Một thanh mốc thời gian — đặt dưới ảnh cột tương ứng (tham chiếu / đối chiếu).
 */
export function RemoteSceneColumnTimeScrubber({
  timeWindow,
  t01,
  onT01Change,
  onIsoChange,
  formatDay,
  ariaLabel,
}: RemoteSceneColumnTimeScrubberProps) {
  const parsed = useMemo(() => parseRemoteWindow(timeWindow), [timeWindow]);

  const onChange = useCallback(
    (v: number) => {
      if (!parsed) return;
      const nextT01 = sliderValueToT01(v);
      onT01Change(nextT01);
      const ms = lerpWindowDateMs(parsed, nextT01);
      onIsoChange(isoDateFromMs(ms));
    },
    [parsed, onT01Change, onIsoChange],
  );

  if (!parsed) return null;

  const clampedT01 = Math.min(1, Math.max(0, t01));
  const currentIso = isoDateFromMs(lerpWindowDateMs(parsed, clampedT01));

  return (
    <div className={TRACK_CLASS}>
      <Slider
        min={0}
        max={REMOTE_SCRUB_SLIDER_STEPS}
        value={t01ToSliderValue(clampedT01)}
        onChange={onChange}
        tooltip={{
          formatter: (v) =>
            v != null
              ? formatDay(isoDateFromMs(lerpWindowDateMs(parsed, sliderValueToT01(Number(v)))))
              : undefined,
        }}
        className="w-full"
        aria-label={ariaLabel}
        aria-valuetext={formatDay(currentIso)}
      />
    </div>
  );
}
